import React, { useEffect, useState } from 'react';
import {
  Edit,
  Plus,
  Search,
  Trash2,
  Calendar,
  Layout,
  TrendingUp,
  AlertCircle,
  MapPin,
  Package,
  FileText,
  Building,
  X,
  RotateCcw
} from 'lucide-react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { formatBookingId } from '../../context/DashboardContext';

const ActivityListing = () => {
  const { userData } = useOutletContext();
  const navigate = useNavigate();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [editPreferredPrice, setEditPreferredPrice] = useState('');
  const [priceFilter, setPriceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [modalPackages, setModalPackages] = useState([]);
  const [meetingPlaceType, setMeetingPlaceType] = useState('preset');
  const [customMeetingPlace, setCustomMeetingPlace] = useState({ name: '', google_map_link: '', address: '' });
  const [selectedPresetIndex, setSelectedPresetIndex] = useState(0);
  const [specialInstruction, setSpecialInstruction] = useState('');

  useEffect(() => {
    fetchDishes();
  }, []);

  const fetchDishes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/task`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token_partner_activities')}`
          }
        }
      );
      setDishes(response.data || []);
      setError(null);
    } catch (error) {
      console.error(error);
      setError('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  const filteredDishes = dishes
    .filter((dish) => {
      const query = searchQuery.toLowerCase().trim();
      if (!query) return true;
      const formattedId = formatBookingId(dish._id);
      return (
        (dish.name || '').toLowerCase().includes(query) ||
        (dish.description || '').toLowerCase().includes(query) ||
        (dish.price || '').toString().includes(query) ||
        formattedId.includes(query)
      );
    })
    .filter((dish) => {
      if (!statusFilter) return true;
      if (statusFilter === 'available') return !dish.is_deleted;
      if (statusFilter === 'unavailable') return dish.is_deleted;
      return true;
    })
    .sort((a, b) => {
      if (priceFilter === 'low-to-high') return a.price - b.price;
      if (priceFilter === 'high-to-low') return b.price - a.price;
      return 0;
    });

  const toggleAvailability = async (id, is_deleted) => {
    try {
      let token = localStorage.getItem('token_partner_activities');
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/task/${id}`,
        { is_deleted: !is_deleted },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDishes(dishes.map((dish) => dish._id === id ? { ...dish, is_deleted: !is_deleted } : dish));
      toast.success('Activity status updated successfully');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error updating status');
    }
  };

  const handleSaveListingConfig = async () => {
    let token = localStorage.getItem('token_partner_activities');
    if (!token) {
      navigate('/login');
      return;
    }

    const payload = {
      packages: modalPackages.map(p => ({
        package_name: p.package_name,
        offered_price: Number(p.offered_price) || 0
      })),
      meeting_place: editActivity?.meeting_place || {},
      special_instruction: editActivity?.special_instruction || '',
      preferred_price: Number(editPreferredPrice) || 0
    };

    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/task/${editActivity._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Listing configuration updated!');
      setEditModalOpen(false);
      fetchDishes();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error updating config');
    }
  };

  const handleDeleteDish = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      let token = localStorage.getItem('token_partner_activities');
      if (!token) {
        navigate('/login');
        return;
      }
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/task/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDishes(dishes.filter((dish) => dish._id !== id));
      toast.success('Activity deleted successfully');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error deleting activity');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-3">
        <div className="w-10 h-10 border-4 border-[#067978] border-t-transparent rounded-full animate-spin"></div>
        <p className="text-slate-500 font-extrabold uppercase tracking-widest text-[10px] animate-pulse">Loading experiences...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 animate-in fade-in duration-700 pb-10">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-[#067978] tracking-tight uppercase">
            Experience Inventory
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Manage and optimize your curated guest experiences.</p>
        </div>
        <button
          onClick={() => {
            navigate('/activity/add-experience');
          }}
          className="px-4 py-2.5 bg-[#067978] text-white rounded-xl flex items-center gap-2 font-extrabold shadow-sm hover:shadow-md hover:scale-105 active:scale-95 transition-all text-xs group uppercase tracking-wider w-fit"
        >
          <Plus size={16} className="group-hover:rotate-90 transition-transform duration-300" />
          <span>Add New Experience</span>
        </button>
      </div>

      {/* Compact Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {[
          { label: 'Live Activities', value: dishes.filter(d => !d.is_deleted).length, icon: Calendar, color: 'emerald' },
          { label: 'Total Listings', value: dishes.length, icon: Layout, color: 'teal' },
          { label: 'Paused', value: dishes.filter(d => d.is_deleted).length, icon: AlertCircle, color: 'amber' },
          { label: 'Growth', value: '+12%', icon: TrendingUp, color: 'indigo' }
        ].map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
              <div className="flex justify-between items-start mb-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{stat.label}</span>
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                  stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                  stat.color === 'teal' ? 'bg-teal-50 text-[#067978]' :
                  stat.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                  'bg-indigo-50 text-indigo-600'
                }`}>
                  <Icon size={16} />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-black text-slate-800 leading-none">{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Compact Filter Bar */}
      <div className="bg-white p-3 rounded-2xl border border-slate-100 shadow-sm mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative group">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#067978] transition-colors" size={15} />
            <input
              type="text"
              placeholder="Search by name, category or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-[#067978]/20 outline-none"
            />
          </div>

          <select
            value={priceFilter}
            onChange={(e) => setPriceFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-700 outline-none cursor-pointer"
          >
            <option value="">Sort Price</option>
            <option value="low-to-high">Low to High</option>
            <option value="high-to-low">High to Low</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border-none rounded-xl text-xs font-semibold text-slate-700 outline-none cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="available">Live Assets</option>
            <option value="unavailable">Paused Assets</option>
          </select>

          {(searchQuery || priceFilter || statusFilter) && (
            <button
              onClick={() => { setSearchQuery(''); setPriceFilter(''); setStatusFilter(''); }}
              className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all flex items-center gap-1 text-[11px] font-bold"
              title="Reset Filters"
            >
              <RotateCcw size={14} />
              <span>Reset</span>
            </button>
          )}
        </div>
      </div>

      {/* Compact Table View Container */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {filteredDishes.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Experience Asset</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Market Rate</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Your Price</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                  <th className="py-3 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredDishes.map((dish) => {
                  const formattedId = formatBookingId(dish._id);
                  return (
                    <tr key={dish._id} className="hover:bg-slate-50/80 transition-colors text-xs group">
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                            {dish.image_url ? (
                              <img src={Array.isArray(dish.image_url) ? dish.image_url[0] : dish.image_url.split(',')[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Layout className="text-slate-300" size={18} />
                            )}
                          </div>
                          <div>
                            <p className="font-extrabold text-slate-900 group-hover:text-[#067978] transition-colors text-xs">{dish.name}</p>
                            <p className="text-[10px] font-mono font-bold text-slate-400 lowercase mt-0.5">id: {formattedId}</p>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-center font-bold text-slate-500 text-xs">
                        ₹{dish.price}
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className="font-black text-slate-900 text-xs">₹{dish.preferred_price || '—'}</span>
                          <button
                            onClick={() => {
                              setEditActivity(dish);
                              setEditPreferredPrice(dish.preferred_price || '');

                              const initialPackages = (dish.superadmin_activity_id?.packages || []).map(gp => {
                                const partnerPkg = (dish.packages || []).find(p => p.package_name === gp.name);
                                return {
                                  package_name: gp.name,
                                  offered_price: partnerPkg ? partnerPkg.offered_price : 0,
                                  price: gp.price,
                                  discount: gp.discount,
                                  duration: gp.duration,
                                  soloOrGroup: gp.soloOrGroup
                                };
                              });
                              setModalPackages(initialPackages);

                              const mp = dish.meeting_place || {};
                              const presets = dish.superadmin_activity_id?.meeting_places || [];
                              const matchingPresetIndex = presets.findIndex(p => p.name === mp.name && p.address === mp.address);
                              const isBusiness = mp.name === dish.business_name && mp.address === dish.address;

                              if (matchingPresetIndex !== -1) {
                                setMeetingPlaceType('preset');
                                setSelectedPresetIndex(matchingPresetIndex);
                              } else if (isBusiness) {
                                setMeetingPlaceType('business');
                              } else if (mp.name || mp.address || mp.google_map_link) {
                                setMeetingPlaceType('custom');
                                setCustomMeetingPlace({
                                  name: mp.name || '',
                                  google_map_link: mp.google_map_link || '',
                                  address: mp.address || ''
                                });
                              } else {
                                setMeetingPlaceType(presets.length > 0 ? 'preset' : 'business');
                                setSelectedPresetIndex(0);
                              }

                              setSpecialInstruction(dish.special_instruction || '');
                              setEditModalOpen(true);
                            }}
                            className="p-1.5 text-[#067978] hover:bg-teal-50 rounded-lg transition-all"
                            title="Configure Listing"
                          >
                            <Edit size={14} />
                          </button>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center">
                          <button
                            onClick={() => toggleAvailability(dish._id, dish.is_deleted)}
                            className={`w-10 h-5 rounded-full p-0.5 transition-all duration-300 relative ${!dish.is_deleted ? 'bg-emerald-500' : 'bg-slate-200'}`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-300 transform ${!dish.is_deleted ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => handleDeleteDish(dish._id)}
                          className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                          title="Delete Listing"
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="p-3 bg-slate-50 rounded-2xl text-slate-300 mb-3"><Calendar size={24} /></div>
            <h3 className="text-sm font-black text-slate-700 uppercase tracking-tight">Empty Inventory</h3>
            <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider max-w-[240px]">Start adding premium experiences to showcase on your digital storefront.</p>
          </div>
        )}
      </div>

      {/* Edit Configuration Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-[200] animate-in fade-in duration-300 px-4 overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl max-w-3xl w-full border border-slate-100 animate-in zoom-in-95 duration-300 my-8">
            <div className="mb-6 flex justify-between items-start">
              <div>
                <h3 className="text-lg font-black text-slate-900 tracking-tight uppercase">Configure Experience Listing</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Configure pricing and package options.</p>
              </div>
              <button onClick={() => setEditModalOpen(false)} className="p-2 bg-slate-50 hover:bg-rose-50 hover:text-rose-500 text-slate-400 rounded-xl transition-all">
                <X size={16} />
              </button>
            </div>
            
            <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2 scrollbar-hide">
              {/* Preferred Price */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 ml-1">
                  Preferred Price (Starting Rate)
                </label>
                <div className="relative group">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-slate-400 text-sm group-focus-within:text-[#067978] transition-colors">₹</span>
                  <input
                    type="number"
                    value={editPreferredPrice}
                    onChange={e => setEditPreferredPrice(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 bg-slate-50 border-none rounded-xl font-bold text-slate-900 text-xs outline-none focus:ring-2 focus:ring-[#067978]/20"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Packages Pricing (Cost Price) */}
              <div className="bg-slate-50/70 p-4 rounded-2xl border border-slate-100 space-y-3">
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Package size={14} className="text-[#067978]" />
                  Package Pricing Configuration (Cost Price)
                </h3>
                <div className="space-y-2">
                  {modalPackages.map((pkg, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-xl border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div>
                        <span className="text-xs font-bold text-slate-900">{pkg.package_name}</span>
                        <div className="flex gap-3 mt-0.5 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                          <span>Market Rate: ₹{pkg.price}</span>
                          {pkg.discount > 0 && <span>Discount: {pkg.discount}%</span>}
                          {pkg.duration && <span>Duration: {pkg.duration}</span>}
                        </div>
                      </div>
                      <div className="relative w-full md:w-40 group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-slate-400 text-xs">₹</span>
                        <input
                          type="number"
                          value={pkg.offered_price || ""}
                          onChange={e => {
                            const updated = modalPackages.map((p, i) => i === idx ? { ...p, offered_price: Number(e.target.value) || 0 } : p);
                            setModalPackages(updated);
                          }}
                          className="w-full pl-7 pr-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg outline-none text-xs font-bold text-slate-700 focus:border-[#067978]"
                          placeholder="Your Cost Price"
                        />
                      </div>
                    </div>
                  ))}
                  {modalPackages.length === 0 && (
                    <p className="text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest py-3">No packages defined for this activity option</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => setEditModalOpen(false)}
                className="flex-1 py-2.5 bg-slate-50 text-slate-400 font-extrabold rounded-xl hover:bg-slate-100 transition-all text-xs uppercase tracking-wider"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveListingConfig}
                className="flex-[2] py-2.5 bg-[#067978] text-white font-extrabold rounded-xl shadow-md hover:scale-105 active:scale-95 transition-all text-xs uppercase tracking-wider"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityListing;
